import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Animated, Image, ImageBackground, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { AvatarGradientColor } from '../../../../typings/types';
import {
	BGHorDivider,
	BtnSidebarClosePng,
	SidebarBackgroundPng,
	SidebarLeftLightPng,
	VipBadgeFramePng,
} from '../../../assets';
import {
	SidebarContentBonus,
	SidebarContentGameRules,
	SidebarContentHelp,
	SidebarContentInvites,
	SidebarContentLogout,
	SidebarContentPersonalData,
	SidebarContentRankings,
	SidebarContentSettings,
	SidebarContentShop,
	SidebarContentTutorial,
} from '../../../assets/sidebar/content';
import { testProps } from '../../../config';
import i18n from '../../../i18n/i18n';
import { deltaDNAService } from '../../../services/deltaDNA/deltadna.service';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { preloadGroup } from '../../../services/preloadAssets';
import { RootState } from '../../../store';
import { logout } from '../../../store/auth/authActions';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { env } from '../../../utils/env';
import { navigateToTab } from '../../../utils/helpers/navigationService';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { RootStackParamList } from '../../navigator';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { TouchableOpacity } from '../common/Touchable';
import { AvatarFrameWithGradient, LoginButton, LoginLogoWeb, Overlay } from '../index';
import LogoutConfirmationModal from '../Modals/LogoutConfirmationModal';
type Nav = NativeStackNavigationProp<RootStackParamList>;

const appPackage = require('../../../../package.json') as { version?: string };
const APP_VERSION = appPackage.version ?? '';

type SidebarButton = {
	label: string;
	icon: any;
	onPress: () => void;
	onPressIn?: () => void;
	testId: string;
};

const Sidebar = ({
	onClose,

	onOpenBonusGamesModal,
}: {
	onClose: () => void;

	onOpenBonusGamesModal: () => void;
}) => {
	const { isMobile, isMd, isSm } = useDevice();
	const { width, insets } = useSafeViewport();
	const player = useSelector((s: RootState) => s.auth.user);

	const SIDEBAR_WIDTH_STYLE = isMobile
		? ('100%' as const)
		: isSm
			? ('100%' as const)
			: isMd
				? ('50%' as const)
				: ('15%' as const);

	const SIDEBAR_WIDTH_ANIM = isMobile || isSm ? width : isMd ? width * 0.5 : width * 0.15;

	const translateX = useRef(new Animated.Value(SIDEBAR_WIDTH_ANIM)).current;

	useEffect(() => {
		Animated.timing(translateX, {
			toValue: 0,
			duration: 580,
			useNativeDriver: true,
		}).start();
	}, [translateX]);

	const closeAnimated = () => {
		Animated.timing(translateX, {
			toValue: SIDEBAR_WIDTH_ANIM,
			duration: 550,
			useNativeDriver: true,
		}).start(({ finished }) => {
			if (finished) onClose();
		});
	};

	const { t } = useTranslation();

	const navigation = useNavigation<Nav>();
	const { navigate } = navigation;
	const isLogged = useSelector(selectIsLogged);

	const dispatch = useDispatch();
	const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
	const onLogout = () => {
		analyticsEvents.trackUserLogout();
		deltaDNAService.reset();
		dispatch(logout());
		navigateToTab(navigation, 'Main');
	};
	const buttons: SidebarButton[] = [
		{
			label: t('T_SITE_MENU_RANKING'),
			icon: SidebarContentRankings,
			testId: 'cta-sidebar-ranking',
			onPressIn: () => preloadGroup('ranking'),
			onPress: () => {
				onClose();
				navigateToTab(navigation, 'Ranking');
			},
		},
		{
			// No sidebar artwork for the achievements yet, so it borrows the rankings trophy the way
			// Friends borrows the invites glyph and Inventory borrows the shop one.
			label: t('T_SITE_ACHIEVEMENTS_TITLE', { defaultValue: 'Achievements' }),
			icon: SidebarContentRankings,
			onPress: () => {
				onClose();
				if (isLogged) {
					navigate('Achievements');
				} else {
					navigate('Login');
				}
			},
		},
		{
			label: t('T_SITE_MENU_INVITES'),
			icon: SidebarContentInvites,
			testId: 'cta-sidebar-invites',
			onPress: () => {
				onClose();
				if (isLogged) {
					navigate('Invites');
				} else {
					navigate('Login');
				}
			},
		},
		{
			label: t('T_SITE_MENU_FRIENDS', { defaultValue: 'Friends' }),
			icon: SidebarContentInvites,
			onPress: () => {
				onClose();
				if (isLogged) {
					navigate('Friends');
				} else {
					navigate('Login');
				}
			},
		},
		{
			// No sidebar artwork for the inventory yet, so it borrows the shop glyph the way
			// Friends borrows the invites one and Event borrows the bonus one.
			label: t('T_SITE_STORE_INVENTORY', { defaultValue: 'Inventory' }),
			icon: SidebarContentShop,
			onPress: () => {
				onClose();
				navigate('Inventory');
			},
		},
		{
			label: t('T_SITE_MENU_BONUSES'),
			icon: SidebarContentBonus,
			testId: 'cta-sidebar-bonuses',
			onPress: onOpenBonusGamesModal,
		},
		{
			label: t('T_SITE_MENU_VIP_ACTIVE_MEMBER', { defaultValue: 'VIP Active Member' }),
			icon: SidebarContentShop,
			onPress: () => {
				onClose();
				navigate('VipActiveMember');
			},
		},
		{
			label: t('T_SITE_MENU_VIP_ROOMS', { defaultValue: 'VIP Rooms' }),
			icon: SidebarContentShop,
			onPress: () => {
				onClose();
				navigate('VipRooms');
			},
		},
		{
			label: t('T_SITE_MENU_EVENT', { defaultValue: 'Event' }),
			icon: SidebarContentBonus,
			onPress: () => {
				onClose();
				navigate('Event');
			},
		},
		{
			label: t('T_SITE_MENU_DUELS'),
			icon: SidebarContentRankings,
			testId: 'cta-sidebar-duels',
			onPress: () => {
				onClose();
				navigate('Duels');
			},
		},
		{
			label: t('T_SITE_MENU_SHOP'),
			icon: SidebarContentShop,
			testId: 'cta-sidebar-shop',
			onPress: () => {
				onClose();
				navigate('BuyPackages', { activeTab: 'chips' });
			},
		},
		{
			label: t('T_SITE_MENU_SETTINGS'),
			icon: SidebarContentSettings,
			testId: 'cta-sidebar-settings',

			onPress: () => {
				onClose();
				navigate('Settings');
			},
		},
		{
			label: t('T_SITE_MENU_PERSONAL_DATA'),
			icon: SidebarContentPersonalData,
			testId: 'cta-sidebar-personal-data',
			onPress: () => {
				onClose();
				navigate('PersonalData');
			},
		},
		{
			label: t('T_SITE_MENU_TUTORIAL'),
			icon: SidebarContentTutorial,
			testId: 'cta-sidebar-tutorial',
			onPress: () => {
				if (isLogged) {
					navigate('Tutorial', { isTutorial: true });
				} else {
					navigate('Login');
				}
			},
		},
		{
			label: t('T_SITE_MENU_RULES'),
			icon: SidebarContentGameRules,
			testId: 'cta-sidebar-rules',
			onPress: () => {
				onClose();
				navigate('GameRules');
			},
		},
		{
			label: t('T_SITE_MENU_CONTACTS'),
			icon: SidebarContentHelp,
			testId: 'cta-sidebar-help',
			onPress: () => {
				onClose();
				navigate('Help');
			},
		},
		isLogged && {
			label: t('T_SITE_MENU_LOGOUT'),
			icon: SidebarContentLogout,
			testId: 'cta-sidebar-logout',
			onPress: () => {
				setShowLogoutConfirmation(true);
			},
		},
	].filter(Boolean) as SidebarButton[];
	const renderButtons = () => {
		return buttons.map(({ label, icon, onPress, onPressIn, testId }) => (
			<TouchableOpacity
				key={label}
				onPress={onPress}
				onPressIn={onPressIn}
				style={styles.sidebarButtonRow}
				{...testProps(testId)}
			>
				<Image source={icon} style={styles.sidebarButtonIcon} />
				<Text style={styles.sidebarButtonLabel}>{label}</Text>
			</TouchableOpacity>
		));
	};

	return (
		<Overlay onClose={closeAnimated}>
			<Animated.View
				style={[
					styles.sidebarPanel,
					{
						width: SIDEBAR_WIDTH_STYLE,
						transform: [{ translateX }],
					},
				]}
			>
				<View style={styles.fill}>
					<ImageBackground source={SidebarBackgroundPng} resizeMode='cover' style={styles.fill}>
						<TouchableOpacity
							onPress={closeAnimated}
							hitSlop={{ top: 20, bottom: 20, left: 20, right: 20 }}
							activeOpacity={0.7}
							style={[
								styles.closeButton,
								{ top: Math.max(22, insets.top + 8), right: Math.max(22, insets.right + 12) },
							]}
						>
							<Image source={BtnSidebarClosePng} style={styles.fill} />
						</TouchableOpacity>

						<View style={{ height: Math.max(20, insets.top + 10) }} />

						{isLogged ? (
							<View style={styles.alignCenter}>
								<TouchableOpacity
									onPress={() => {
										onClose();
										navigate('Profile');
									}}
									activeOpacity={0.85}
									{...testProps('cta-sidebar-profile')}
								>
									<View style={styles.relative}>
										<AvatarFrameWithGradient
											gradientColor={AvatarGradientColor.Blue}
											sizeMultiplier={1.6}
											showUsernameFrame={true}
										/>
										{player?.isVip && <Image source={VipBadgeFramePng} style={styles.vipBadge} />}
									</View>
								</TouchableOpacity>
							</View>
						) : (
							<View style={styles.alignCenter}>
								<LoginLogoWeb scale={0.5} />
								<LoginButton style={styles.loginButtonOffset} />
							</View>
						)}
						<Image source={BGHorDivider} style={styles.horizontalDivider} />
						<IndicatorScrollView contentContainerStyle={[styles.scrollContent, { paddingTop: 8 }]}>
							{renderButtons()}
						</IndicatorScrollView>
						<VersionFooter bottomInset={insets.bottom} />
					</ImageBackground>

					<Image source={SidebarLeftLightPng} style={styles.leftLight} />
				</View>
			</Animated.View>

			<LogoutConfirmationModal
				visible={showLogoutConfirmation}
				onClose={() => setShowLogoutConfirmation(false)}
				onConfirm={() => {
					setShowLogoutConfirmation(false);
					onClose();
					onLogout();
				}}
			/>
		</Overlay>
	);
};

const copyToClipboard = (text: string) => {
	if (navigator?.clipboard?.writeText) {
		navigator.clipboard.writeText(text).catch(() => {});
		return;
	}
	try {
		const mod = require('@react-native-clipboard/clipboard');
		const Clipboard = mod?.default ?? mod;
		if (Clipboard?.setString) {
			Clipboard.setString(text);
			return;
		}
	} catch {}
	Alert.alert(i18n.t('T_SITE_APP_VERSION', { defaultValue: 'Version' }), text);
};

const VersionFooter = ({ bottomInset }: { bottomInset: number }) => {
	const isDevMode = env('DEV_MODE', 'false') === 'true';
	const versionText = `v${APP_VERSION}`;

	const handlePress = useCallback(() => {
		if (isDevMode) copyToClipboard(versionText);
	}, [isDevMode, versionText]);

	return (
		<View style={[styles.versionFooter, { bottom: Math.max(16, bottomInset + 8) }]}>
			{isDevMode ? (
				<TouchableOpacity onPress={handlePress}>
					<Text style={styles.versionText}>{versionText}</Text>
				</TouchableOpacity>
			) : (
				<Text style={styles.versionText}>{versionText}</Text>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	sidebarButtonRow: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 0,
	},
	sidebarButtonIcon: { width: 45, height: 45, marginRight: 10 },
	sidebarButtonLabel: { fontSize: 14, color: 'white' },
	sidebarPanel: {
		position: 'absolute',
		top: 0,
		right: 0,
		height: '100%',
		zIndex: 1000,
		elevation: 10,
	},
	fill: {
		width: '100%',
		height: '100%',
	},
	closeButton: {
		zIndex: 999,
		position: 'absolute',
		width: 25,
		height: 25,
		top: 22,
		right: 22,
		elevation: 10,
	},
	alignCenter: { alignItems: 'center' },
	relative: { position: 'relative' },
	vipBadge: {
		position: 'absolute',
		top: 64,
		right: 14,
		width: 35,
		height: 35,
		zIndex: 22,
	},
	scrollContent: { padding: 20 },
	versionFooter: {
		position: 'absolute',
		bottom: 16,
		left: 0,
		right: 0,
		alignItems: 'center',
	},
	versionText: {
		color: '#ffffff',
		fontSize: 12,
		opacity: 0.8,
	},
	leftLight: {
		position: 'absolute',
		width: 40,
		height: '100%',
		left: -20,
	},
	loginButtonOffset: {
		marginTop: -30,
	},
	horizontalDivider: {
		height: 10,
		aspectRatio: 300 / 10,
		marginTop: 8,
	},
});

export default Sidebar;
